import * as React from 'react'
import { Link } from 'react-router-dom'

import { Site } from '@envato/foundation-types'

import * as styles from './categoryTilesBlock.module.css'

import { reportBlockClick } from '../../../../common/analytics/homePageAnalytics'
import { config } from '../../../../common/config'
import SearchLink from '../../../containers/searchLinkContainer'
import Button from '../../button'
import SVGSpriteWrapper from '../../svgSpriteWrapper'

import EnhancedLazyLoad from '../../../components/enhancedLazyLoad'
import ImagePlaceholder from '../../../components/imagePlaceholder'
import FeatureContainer from '../../../containers/feature'

interface ITile {
  title: string
  subtitle: string
  category: string
}

interface ICta {
  title: string
  url: string
}

interface IProps {
  currentSite: Site
  data: ITile[]
  cta: ICta
}

const colors = new Map([
  ['default', '#d5d5ff'],
  ['wordpress', '#d5d5ff'],
  ['ecommerce', '#ffd6b0'],
  ['site-templates', '#beebf6'],
  ['marketing', '#fff48a'],
  ['cms-themes', '#d2fae6'],
  ['blogging', '#d5d5ff']
])

const placeholderRatio = 300 / 590

const CategoryTilesBlock = ({ data, cta, currentSite }: IProps) => {
  const IMG_PATH = `${
    config.assets.PUBLIC_PATH
  }/images/home-page/new-design/${currentSite}`

  return (
    <section className={styles.root}>
      <div className={styles.wrapper}>
        <div className={styles.tiles}>
          {data.map((item, index) => {
            const imageUrl = `${IMG_PATH}/category-tiles-${item.category}.png`
            const bgColor = colors.get(item.category) || colors.get('default')
            const locationParams = { category: item.category }

            return (
              <div key={index} className={styles.tile}>
                <div className={styles.tileDetail}>
                  <h2>
                    <SearchLink
                      locationParams={locationParams}
                      className={styles.tileTitle}
                      title={`View ${item.title}`}
                      onClick={() =>
                        reportBlockClick('Category Tiles', {
                          eventLabel: item.category,
                          dimension19: 'body',
                          eventAction: 'click;tiles'
                        })
                      }
                    >
                      {item.title}
                    </SearchLink>
                  </h2>

                  <p className={styles.tileSubtitle}>{item.subtitle}</p>

                  <SearchLink
                    locationParams={{ ...locationParams, sort: 'date' }}
                    className={styles.tileLink}
                    title={`Newest ${item.title}`}
                    onClick={() =>
                      reportBlockClick('Category Tiles', {
                        eventLabel: item.category,
                        dimension19: 'body',
                        eventAction: 'click;tiles'
                      })
                    }
                  >
                    Newest
                  </SearchLink>

                  <SearchLink
                    locationParams={{ ...locationParams, sort: 'sales' }}
                    className={styles.tileLink}
                    title={`Bestselling ${item.title}`}
                    onClick={() =>
                      reportBlockClick('Category Tiles', {
                        eventLabel: item.category,
                        dimension19: 'body',
                        eventAction: 'click;tiles'
                      })
                    }
                  >
                    Bestsellers
                  </SearchLink>
                </div>

                <SearchLink
                  className={styles.tileArt}
                  locationParams={locationParams}
                  title={`View ${item.title}`}
                >
                  <FeatureContainer name="homepage:lazyload">
                    <EnhancedLazyLoad
                      offset={0}
                      placeholder={
                        <ImagePlaceholder
                          ratio={placeholderRatio}
                          className={styles.tileImage}
                        />
                      }
                    >
                      <img
                        className={styles.tileImage}
                        src={imageUrl}
                        alt={item.title}
                      />
                    </EnhancedLazyLoad>
                  </FeatureContainer>

                  <FeatureContainer name="!homepage:lazyload">
                    <img
                      className={styles.tileImage}
                      src={imageUrl}
                      alt={item.title}
                    />
                  </FeatureContainer>

                  <div className={styles.tileIcon}>
                    <SVGSpriteWrapper.SVG
                      size="large"
                      iconId={`#${currentSite}-svg-${item.category}`}
                      fallbackId={`#${currentSite}-svg-generic`}
                      backgroundColor={bgColor}
                    />
                  </div>
                </SearchLink>
              </div>
            )
          })}
        </div>

        <Button
          type={Button.Type.Primary}
          className={styles.cta}
          to={cta.url}
          renderAs={Link}
          onClick={() =>
            reportBlockClick('Category Tiles', {
              eventLabel: cta.title,
              dimension19: 'body'
            })
          }
        >
          {cta.title}
        </Button>
      </div>
    </section>
  )
}

export default CategoryTilesBlock
